/**
 * ReauthDialog
 *
 * Shown when a sensitive API call (or the freshness probe) reports 403 REAUTHENTICATION_REQUIRED.
 * The landlord stays signed in: this only re-confirms who they are — passkey, emailed code, or emailed
 * magic link — using the email already on the session (nothing to type), then returns them to the
 * page they were on. It never logs them out and never navigates to the login page.
 */

import { useState, type FormEvent } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { createLogger } from '@resida/shared';
import { useAuth } from '../../hooks/useAuth';
import {
  ApiClientError,
  managerLoginOptions,
  managerLoginVerify,
  managerRequestEmailCode,
  managerRequestMagicLink,
  managerVerifyEmailCode,
} from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';

const logger = createLogger('landlord:components:auth:ReauthDialog');

type Step = 'choose' | 'code' | 'link-sent';

interface ReauthDialogProps {
  /** App-relative path of the page that needs the fresh session (BrowserRouter basename stripped). */
  returnTo: string;
  /** Leave without re-authenticating (the blocked action is abandoned). */
  onCancel: () => void;
}

const BASE_PATH = '/landlord';

function errorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === 'INVALID_CODE' || err.code === 'CODE_EXPIRED') {
      return 'Invalid or expired code. Please try again or request a new code.';
    }
    if (err.code === 'CODE_ALREADY_USED') return 'This code has already been used. Please request a new code.';
    return err.message;
  }
  if (err instanceof Error) {
    if (err.name === 'NotAllowedError') return 'Authentication was cancelled. Please try again.';
    if (err.name === 'InvalidStateError') return 'No passkey found for this device.';
    if (err.name === 'NotSupportedError') return 'Your browser or device does not support passkeys.';
    return err.message || 'Authentication failed. Please try again.';
  }
  return 'An unexpected error occurred. Please try again.';
}

export function ReauthDialog({ returnTo, onCancel }: ReauthDialogProps) {
  const { manager } = useAuth();
  const email = manager?.email ?? '';

  const [step, setStep] = useState<Step>('choose');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Re-authenticated: reload the page they were on so every request runs under the fresh session. */
  const finish = () => window.location.assign(BASE_PATH + returnTo);

  async function run(action: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (err) {
      logger.error('Re-authentication failed:', err);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const usePasskey = () =>
    run(async () => {
      const { options, sessionId } = await managerLoginOptions();
      const credential = await startAuthentication({ optionsJSON: options });
      await managerLoginVerify(credential, sessionId);
      finish();
    });

  const sendCode = () =>
    run(async () => {
      await managerRequestEmailCode(email);
      setCode('');
      setStep('code');
    });

  const sendLink = () =>
    run(async () => {
      await managerRequestMagicLink(email, returnTo);
      setStep('link-sent');
    });

  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    void run(async () => {
      await managerVerifyEmailCode(email, code);
      finish();
    });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <DialogContent className="max-w-sm w-[90%]" data-testid="reauth-dialog">
        <DialogTitle className="text-xl leading-normal">Confirm it&apos;s you</DialogTitle>

        {error && (
          <div
            className="text-sm text-destructive p-3 bg-destructive/5 border border-destructive/20 rounded-md mt-3"
            role="alert"
            data-testid="reauth-error"
          >
            {error}
          </div>
        )}

        {step === 'choose' && (
          <>
            <DialogDescription className="text-base mt-3">
              For your security, this action needs a recent sign-in. Confirm it&apos;s you and you&apos;ll
              come right back to what you were doing.
            </DialogDescription>
            <div className="flex flex-col gap-3 mt-6">
              <Button onClick={usePasskey} disabled={busy} aria-busy={busy} className="w-full" data-testid="reauth-passkey">
                {busy ? 'Authenticating...' : 'Use passkey'}
              </Button>
              {email && (
                <>
                  <Button variant="outline" onClick={sendCode} disabled={busy} className="w-full" data-testid="reauth-email-code">
                    Email me a code
                  </Button>
                  <Button variant="outline" onClick={sendLink} disabled={busy} className="w-full" data-testid="reauth-magic-link">
                    Email me a magic link
                  </Button>
                </>
              )}
              <Button variant="ghost" onClick={onCancel} disabled={busy} className="w-full" data-testid="reauth-cancel">
                Cancel
              </Button>
            </div>
          </>
        )}

        {step === 'code' && (
          <form onSubmit={verifyCode} className="mt-3">
            <DialogDescription className="text-base">
              We emailed a 6-digit code to <strong>{email}</strong>.
            </DialogDescription>
            <label htmlFor="reauth-code" className="block text-sm font-medium text-muted-foreground mt-4 mb-1.5">
              6-digit code
            </label>
            <input
              id="reauth-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={busy}
              className="w-full min-h-[44px] px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="reauth-code-input"
            />
            <div className="flex flex-col gap-3 mt-4">
              <Button type="submit" disabled={busy || code.length < 6} className="w-full" data-testid="reauth-code-submit">
                {busy ? 'Verifying...' : 'Verify'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setError(null); setStep('choose'); }} disabled={busy} className="w-full">
                Back
              </Button>
            </div>
          </form>
        )}

        {step === 'link-sent' && (
          <div className="mt-3" data-testid="reauth-link-sent">
            <DialogDescription className="text-base">
              We sent a magic link to <strong>{email}</strong>. Open it on this device and you&apos;ll be
              brought back to this page. The link expires in 15 minutes.
            </DialogDescription>
            <Button variant="ghost" onClick={() => { setError(null); setStep('choose'); }} className="w-full mt-4">
              Back
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
